import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

const resources = {
  fr: {
    translation: {
      support: {
        title: "Assistant BuildConnect",
        subtitle: "Réponses immédiates, 7j/7",
        contactTitle: "Contacter l'administrateur",
        contactSubtitle: "Réponse par email sous 24-48h",
        placeholder: "Posez votre question…",
        typing: "L'assistant rédige…",
        escalate: "Laisser un message à l'administrateur",
        contactLink: "Pas de réponse ? Écrire à l'administrateur",
        contactIntro: "Décrivez votre demande : elle sera envoyée par email à l'administrateur de BuildConnect avec l'historique de cette conversation.",
        name: "Votre nom / société",
        email: "Votre email",
        message: "Votre question ou problème…",
        send: "Envoyer à l'administrateur",
        sentTitle: "Message transmis !",
        sentBody: "L'administrateur vous répondra à",
        backToChat: "Retour au chat",
        error: "Désolé, je n'arrive pas à répondre pour le moment. Vous pouvez laisser un message à l'administrateur.",
        help: "Aide"
      },
      // Navigation
      nav: {
        home: "Accueil",
        jobs: "Chantiers",
        contractors: "Sous-traitants",
        dashboard: "Tableau de bord",
        login: "Connexion",
        register: "Inscription",
        logout: "Déconnexion",
        messages: "Messages",
        profile: "Profil",
        admin: "Admin"
      },
      // Hero
      hero: {
        title: "La plateforme qui connecte les entreprises du bâtiment",
        subtitle: "Trouvez des sous-traitants qualifiés ou des chantiers dans votre région en Belgique, Luxembourg et France.",
        findJob: "Trouver un chantier",
        findContractor: "Trouver un sous-traitant"
      },
      // Common
      common: {
        search: "Rechercher",
        filter: "Filtrer",
        apply: "Postuler",
        contact: "Contacter",
        viewProfile: "Voir le profil",
        viewDetails: "Voir les détails",
        save: "Enregistrer",
        cancel: "Annuler",
        delete: "Supprimer",
        edit: "Modifier",
        loading: "Chargement...",
        noResults: "Aucun résultat",
        verified: "Vérifié",
        premium: "Premium",
        urgent: "Urgent",
        available: "Disponible",
        unavailable: "Indisponible"
      },
      // Auth
      auth: {
        email: "Email",
        password: "Mot de passe",
        confirmPassword: "Confirmer le mot de passe",
        companyName: "Nom de l'entreprise",
        accountType: "Type de compte",
        company: "Entreprise (publie des chantiers)",
        contractor: "Sous-traitant (cherche des chantiers)",
        loginTitle: "Connexion",
        registerTitle: "Créer un compte",
        forgotPassword: "Mot de passe oublié?",
        noAccount: "Pas de compte?",
        hasAccount: "Déjà un compte?",
        googleLogin: "Continuer avec Google",
        orContinueWith: "ou continuer avec"
      },
      // Jobs
      jobs: {
        title: "Chantiers disponibles",
        postJob: "Publier un chantier",
        myJobs: "Mes chantiers",
        applications: "Candidatures",
        jobTitle: "Titre du chantier",
        description: "Description",
        location: "Localisation",
        city: "Ville",
        country: "Pays",
        trade: "Corps de métier",
        startDate: "Date de début",
        duration: "Durée",
        budget: "Budget",
        budgetRange: "Fourchette de budget",
        photos: "Photos",
        documents: "Documents",
        markUrgent: "Marquer comme urgent",
        urgentJobs: "Chantiers urgents",
        applyNow: "Postuler maintenant",
        applicationMessage: "Message de candidature",
        proposedBudget: "Budget proposé"
      },
      // Contractors
      contractors: {
        title: "Sous-traitants",
        trades: "Corps de métier",
        experience: "Années d'expérience",
        serviceRadius: "Rayon d'intervention",
        certifications: "Certifications",
        portfolio: "Portfolio",
        reviews: "Avis",
        rating: "Note",
        availability: "Disponibilité"
      },
      // Profile
      profile: {
        title: "Mon profil",
        vatNumber: "Numéro de TVA",
        phone: "Téléphone",
        address: "Adresse",
        description: "Description de l'entreprise",
        updateProfile: "Mettre à jour le profil"
      },
      // Dashboard
      dashboard: {
        title: "Tableau de bord",
        overview: "Aperçu",
        myJobs: "Mes chantiers",
        myApplications: "Mes candidatures",
        messages: "Messages",
        reviews: "Avis reçus",
        subscription: "Abonnement",
        settings: "Paramètres"
      },
      // Subscription
      subscription: {
        title: "Abonnement",
        freePlan: "Plan Gratuit",
        premiumPlan: "Plan Premium",
        currentPlan: "Plan actuel",
        upgrade: "Passer à Premium",
        features: {
          freeJobs: "3 publications de chantiers",
          freeApplications: "5 candidatures",
          premiumJobs: "Publications illimitées",
          premiumApplications: "Candidatures illimitées",
          premiumMessaging: "Messagerie directe",
          premiumVisibility: "Visibilité prioritaire",
          premiumBadge: "Badge vérifié"
        },
        monthly: "Mensuel",
        yearly: "Annuel",
        perMonth: "/mois",
        perYear: "/an"
      },
      // Reviews
      reviews: {
        title: "Avis",
        writeReview: "Écrire un avis",
        reliability: "Fiabilité",
        communication: "Communication",
        quality: "Qualité du travail",
        deadlines: "Respect des délais",
        comment: "Commentaire"
      },
      // Map
      map: {
        title: "Carte interactive",
        showJobs: "Afficher les chantiers",
        showContractors: "Afficher les sous-traitants"
      },
      // How it works
      howItWorks: {
        title: "Comment ça marche",
        step1Title: "Créez votre profil",
        step1Desc: "Inscrivez-vous et complétez votre profil d'entreprise ou de sous-traitant.",
        step2Title: "Publiez ou recherchez",
        step2Desc: "Publiez vos chantiers ou recherchez des opportunités dans votre région.",
        step3Title: "Connectez-vous",
        step3Desc: "Échangez avec les professionnels et trouvez le bon partenaire.",
        step4Title: "Collaborez",
        step4Desc: "Travaillez ensemble et développez votre réseau professionnel."
      },
      // Trades
      trades: {
        masonry: "Maçonnerie",
        general_contractor: "Entreprise générale",
        roofing: "Toiture",
        plumbing: "Plomberie",
        electrical: "Électricité",
        carpentry: "Menuiserie",
        plastering: "Plâtrerie",
        tiling: "Carrelage",
        painting: "Peinture",
        screed: "Chape",
        hvac: "Chauffage/Climatisation",
        demolition: "Démolition",
        earthwork: "Terrassement",
        insulation: "Isolation",
        fitting_out: "Aménagement",
        pool: "Piscine",
        facade: "Façade",
        peb: "PEB",
        site_management: "Gestion de chantier",
        safety_coordinator: "Coordinateur sécurité",
        architect: "Architecte"
      },
      // Admin
      admin: {
        title: "Administration",
        users: "Utilisateurs",
        allJobs: "Tous les chantiers",
        analytics: "Statistiques",
        verify: "Vérifier",
        suspend: "Suspendre"
      }
    }
  },
  nl: {
    translation: {
      support: {
        title: "BuildConnect-assistent",
        subtitle: "Directe antwoorden, 7 dagen op 7",
        contactTitle: "Contact met de beheerder",
        contactSubtitle: "Antwoord per e-mail binnen 24-48u",
        placeholder: "Stel uw vraag…",
        typing: "De assistent schrijft…",
        escalate: "Een bericht achterlaten voor de beheerder",
        contactLink: "Geen antwoord? Schrijf naar de beheerder",
        contactIntro: "Beschrijf uw vraag: ze wordt per e-mail naar de BuildConnect-beheerder gestuurd, samen met deze gespreksgeschiedenis.",
        name: "Uw naam / bedrijf",
        email: "Uw e-mailadres",
        message: "Uw vraag of probleem…",
        send: "Verzenden naar de beheerder",
        sentTitle: "Bericht verzonden!",
        sentBody: "De beheerder antwoordt u op",
        backToChat: "Terug naar de chat",
        error: "Sorry, ik kan momenteel niet antwoorden. U kunt een bericht achterlaten voor de beheerder.",
        help: "Hulp"
      },
      nav: {
        home: "Home",
        jobs: "Werven",
        contractors: "Onderaannemers",
        dashboard: "Dashboard",
        login: "Inloggen",
        register: "Registreren",
        logout: "Uitloggen",
        messages: "Berichten",
        profile: "Profiel",
        admin: "Admin"
      },
      hero: {
        title: "Het platform dat bouwbedrijven verbindt",
        subtitle: "Vind gekwalificeerde onderaannemers of werven in uw regio in België, Luxemburg en Frankrijk.",
        findJob: "Zoek een werf",
        findContractor: "Zoek een onderaannemer"
      },
      common: {
        search: "Zoeken",
        filter: "Filteren",
        apply: "Solliciteren",
        contact: "Contact",
        viewProfile: "Profiel bekijken",
        viewDetails: "Details bekijken",
        save: "Opslaan",
        cancel: "Annuleren",
        delete: "Verwijderen",
        edit: "Bewerken",
        loading: "Laden...",
        noResults: "Geen resultaten",
        verified: "Geverifieerd",
        premium: "Premium",
        urgent: "Dringend",
        available: "Beschikbaar",
        unavailable: "Niet beschikbaar"
      },
      auth: {
        email: "Email",
        password: "Wachtwoord",
        confirmPassword: "Bevestig wachtwoord",
        companyName: "Bedrijfsnaam",
        accountType: "Accounttype",
        company: "Bedrijf (plaatst werven)",
        contractor: "Onderaannemer (zoekt werven)",
        loginTitle: "Inloggen",
        registerTitle: "Account aanmaken",
        forgotPassword: "Wachtwoord vergeten?",
        noAccount: "Geen account?",
        hasAccount: "Al een account?",
        googleLogin: "Doorgaan met Google",
        orContinueWith: "of doorgaan met"
      },
      jobs: {
        title: "Beschikbare werven",
        postJob: "Werf plaatsen",
        myJobs: "Mijn werven",
        applications: "Sollicitaties",
        jobTitle: "Titel van de werf",
        description: "Beschrijving",
        location: "Locatie",
        city: "Stad",
        country: "Land",
        trade: "Vakgebied",
        startDate: "Startdatum",
        duration: "Duur",
        budget: "Budget",
        budgetRange: "Budgetbereik",
        photos: "Foto's",
        documents: "Documenten",
        markUrgent: "Markeren als dringend",
        urgentJobs: "Dringende werven",
        applyNow: "Nu solliciteren",
        applicationMessage: "Sollicitatiebericht",
        proposedBudget: "Voorgesteld budget"
      },
      contractors: {
        title: "Onderaannemers",
        trades: "Vakgebieden",
        experience: "Jaren ervaring",
        serviceRadius: "Werkgebied",
        certifications: "Certificeringen",
        portfolio: "Portfolio",
        reviews: "Beoordelingen",
        rating: "Beoordeling",
        availability: "Beschikbaarheid"
      },
      profile: {
        title: "Mijn profiel",
        vatNumber: "BTW-nummer",
        phone: "Telefoon",
        address: "Adres",
        description: "Bedrijfsbeschrijving",
        updateProfile: "Profiel bijwerken"
      },
      dashboard: {
        title: "Dashboard",
        overview: "Overzicht",
        myJobs: "Mijn werven",
        myApplications: "Mijn sollicitaties",
        messages: "Berichten",
        reviews: "Ontvangen beoordelingen",
        subscription: "Abonnement",
        settings: "Instellingen"
      },
      subscription: {
        title: "Abonnement",
        freePlan: "Gratis plan",
        premiumPlan: "Premium plan",
        currentPlan: "Huidig plan",
        upgrade: "Upgrade naar Premium",
        features: {
          freeJobs: "3 werfplaatsingen",
          freeApplications: "5 sollicitaties",
          premiumJobs: "Onbeperkte plaatsingen",
          premiumApplications: "Onbeperkte sollicitaties",
          premiumMessaging: "Directe berichten",
          premiumVisibility: "Prioriteitsvisibiliteit",
          premiumBadge: "Geverifieerde badge"
        },
        monthly: "Maandelijks",
        yearly: "Jaarlijks",
        perMonth: "/maand",
        perYear: "/jaar"
      },
      reviews: {
        title: "Beoordelingen",
        writeReview: "Schrijf een beoordeling",
        reliability: "Betrouwbaarheid",
        communication: "Communicatie",
        quality: "Kwaliteit van werk",
        deadlines: "Naleven van deadlines",
        comment: "Commentaar"
      },
      map: {
        title: "Interactieve kaart",
        showJobs: "Werven tonen",
        showContractors: "Onderaannemers tonen"
      },
      howItWorks: {
        title: "Hoe werkt het",
        step1Title: "Maak uw profiel",
        step1Desc: "Registreer en vul uw bedrijfs- of onderaannemersprofiel in.",
        step2Title: "Plaats of zoek",
        step2Desc: "Plaats uw werven of zoek naar kansen in uw regio.",
        step3Title: "Maak contact",
        step3Desc: "Communiceer met professionals en vind de juiste partner.",
        step4Title: "Werk samen",
        step4Desc: "Werk samen en breid uw professionele netwerk uit."
      },
      trades: {
        masonry: "Metselwerk",
        general_contractor: "Algemene aannemer",
        roofing: "Dakwerk",
        plumbing: "Loodgieterij",
        electrical: "Elektriciteit",
        carpentry: "Schrijnwerk",
        plastering: "Stukadoor",
        tiling: "Tegelwerk",
        painting: "Schilderwerk",
        screed: "Dekvloer",
        hvac: "Verwarming/Airco",
        demolition: "Sloop",
        earthwork: "Grondwerk",
        insulation: "Isolatie",
        fitting_out: "Inrichting",
        pool: "Zwembad",
        facade: "Gevel",
        peb: "EPB",
        site_management: "Werfbeheer",
        safety_coordinator: "Veiligheidscoördinator",
        architect: "Architect"
      },
      admin: {
        title: "Administratie",
        users: "Gebruikers",
        allJobs: "Alle werven",
        analytics: "Statistieken",
        verify: "Verifiëren",
        suspend: "Opschorten"
      }
    }
  },
  lu: {
    translation: {
      support: {
        title: "BuildConnect-Assistent",
        subtitle: "Direkt Äntwerten, 7 Deeg op 7",
        contactTitle: "Den Administrateur kontaktéieren",
        contactSubtitle: "Äntwert per E-Mail bannent 24-48h",
        placeholder: "Stellt Är Fro…",
        typing: "Den Assistent schreift…",
        escalate: "Eng Noriicht fir den Administrateur hannerloossen",
        contactLink: "Keng Äntwert? Dem Administrateur schreiwen",
        contactIntro: "Beschreift Är Ufro: si gëtt per E-Mail un den BuildConnect-Administrateur geschéckt, zesumme mam Verlaf vun dësem Gespréich.",
        name: "Ären Numm / Är Firma",
        email: "Är E-Mail",
        message: "Är Fro oder Äre Problem…",
        send: "Un den Administrateur schécken",
        sentTitle: "Noriicht geschéckt!",
        sentBody: "Den Administrateur äntwert Iech op",
        backToChat: "Zréck op de Chat",
        error: "Pardon, ech kann de Moment net äntweren. Dir kënnt eng Noriicht fir den Administrateur hannerloossen.",
        help: "Hëllef"
      },
      nav: {
        home: "Heem",
        jobs: "Chantieren",
        contractors: "Ënnernehmer",
        dashboard: "Dashboard",
        login: "Aloggen",
        register: "Umellen",
        logout: "Ausloggen",
        messages: "Messagen",
        profile: "Profil",
        admin: "Admin"
      },
      hero: {
        title: "D'Plattform déi Baufirmen verbënnt",
        subtitle: "Fannt qualifizéiert Ënnernehmer oder Chantieren an Ärer Regioun a Belsch, Lëtzebuerg a Frankräich.",
        findJob: "Chantier fannen",
        findContractor: "Ënnernehmer fannen"
      },
      common: {
        search: "Sichen",
        filter: "Filteren",
        apply: "Bewerben",
        contact: "Kontakt",
        viewProfile: "Profil kucken",
        viewDetails: "Detailer kucken",
        save: "Späicheren",
        cancel: "Ofbriechen",
        delete: "Läschen",
        edit: "Änneren",
        loading: "Lueden...",
        noResults: "Keng Resultater",
        verified: "Verifizéiert",
        premium: "Premium",
        urgent: "Dréngend",
        available: "Disponibel",
        unavailable: "Net disponibel"
      },
      auth: {
        email: "Email",
        password: "Passwuert",
        confirmPassword: "Passwuert bestätegen",
        companyName: "Firmennumm",
        accountType: "Konto Typ",
        company: "Firma (publizéiert Chantieren)",
        contractor: "Ënnernehmer (sicht Chantieren)",
        loginTitle: "Aloggen",
        registerTitle: "Konto erstellen",
        forgotPassword: "Passwuert vergiess?",
        noAccount: "Keen Konto?",
        hasAccount: "Schonn e Konto?",
        googleLogin: "Mat Google weiderfueren",
        orContinueWith: "oder weiderfueren mat"
      },
      jobs: {
        title: "Verfügbar Chantieren",
        postJob: "Chantier publizéieren",
        myJobs: "Meng Chantieren",
        applications: "Kandidaturen",
        jobTitle: "Titel vum Chantier",
        description: "Beschreiwung",
        location: "Standuert",
        city: "Stad",
        country: "Land",
        trade: "Handwierk",
        startDate: "Startdatum",
        duration: "Dauer",
        budget: "Budget",
        budgetRange: "Budget Beräich",
        photos: "Fotoen",
        documents: "Dokumenter",
        markUrgent: "Als dréngend markéieren",
        urgentJobs: "Dréngend Chantieren",
        applyNow: "Elo bewerben",
        applicationMessage: "Bewerbungsmessage",
        proposedBudget: "Proposéiert Budget"
      },
      contractors: {
        title: "Ënnernehmer",
        trades: "Handwierker",
        experience: "Joer Erfarung",
        serviceRadius: "Service Radius",
        certifications: "Zertifizéierungen",
        portfolio: "Portfolio",
        reviews: "Bewäertungen",
        rating: "Bewäertung",
        availability: "Disponibilitéit"
      },
      profile: {
        title: "Mäi Profil",
        vatNumber: "TVA Nummer",
        phone: "Telefon",
        address: "Adress",
        description: "Firma Beschreiwung",
        updateProfile: "Profil aktualiséieren"
      },
      dashboard: {
        title: "Dashboard",
        overview: "Iwwerbléck",
        myJobs: "Meng Chantieren",
        myApplications: "Meng Kandidaturen",
        messages: "Messagen",
        reviews: "Kritt Bewäertungen",
        subscription: "Abonnement",
        settings: "Astellungen"
      },
      subscription: {
        title: "Abonnement",
        freePlan: "Gratis Plang",
        premiumPlan: "Premium Plang",
        currentPlan: "Aktuellen Plang",
        upgrade: "Op Premium wiesselen",
        features: {
          freeJobs: "3 Chantier Publikatiounen",
          freeApplications: "5 Kandidaturen",
          premiumJobs: "Onlimitéiert Publikatiounen",
          premiumApplications: "Onlimitéiert Kandidaturen",
          premiumMessaging: "Direkt Messagen",
          premiumVisibility: "Prioritéit Visibilitéit",
          premiumBadge: "Verifizéiert Badge"
        },
        monthly: "Méintlech",
        yearly: "Jäerlech",
        perMonth: "/Mount",
        perYear: "/Joer"
      },
      reviews: {
        title: "Bewäertungen",
        writeReview: "Bewäertung schreiwen",
        reliability: "Zouverlässegkeet",
        communication: "Kommunikatioun",
        quality: "Qualitéit vun der Aarbecht",
        deadlines: "Respekt vun Deadlines",
        comment: "Kommentar"
      },
      map: {
        title: "Interaktiv Kaart",
        showJobs: "Chantieren weisen",
        showContractors: "Ënnernehmer weisen"
      },
      howItWorks: {
        title: "Wéi et fonctionnéiert",
        step1Title: "Profil erstellen",
        step1Desc: "Umellen an Äre Firma oder Ënnernehmer Profil ausfëllen.",
        step2Title: "Publizéieren oder Sichen",
        step2Desc: "Publizéiert Är Chantieren oder sicht no Méiglechkeeten an Ärer Regioun.",
        step3Title: "Kontakt ophuelen",
        step3Desc: "Austausch mat Professionellen a fannt de richtege Partner.",
        step4Title: "Zesummenaarbecht",
        step4Desc: "Schafft zesummen an entwéckelt Äert professionellt Netzwierk."
      },
      trades: {
        masonry: "Maurerei",
        general_contractor: "Generalunternehmer",
        roofing: "Daachdeckung",
        plumbing: "Sanitär",
        electrical: "Elektresch",
        carpentry: "Schreinerei",
        plastering: "Gipser",
        tiling: "Flisser",
        painting: "Malerei",
        screed: "Estrich",
        hvac: "Heizung/Klima",
        demolition: "Ofbroch",
        earthwork: "Äerdaarbechten",
        insulation: "Isolatioun",
        fitting_out: "Amenagement",
        pool: "Schwämm",
        facade: "Fassad",
        peb: "PEB",
        site_management: "Chantiers-Gestioun",
        safety_coordinator: "Sécherheetskoordinator",
        architect: "Architekt"
      },
      admin: {
        title: "Administratioun",
        users: "Benotzer",
        allJobs: "All Chantieren",
        analytics: "Statistiken",
        verify: "Verifizéieren",
        suspend: "Suspendéieren"
      }
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'fr',
    interpolation: {
      escapeValue: false
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage']
    }
  });

export default i18n;
